import React, { useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell } from 'docx';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { schoolFeeAPI } from '../services/api';
import '../CSS/FeeExport.css'

function FeeExport({ filteredSubmittedFees, formatNumber, getSchoolFeeByClass, showToastMessage }) {

  const [isOpen, setIsOpen] = useState(false);

  // export functions will go here
  const fallbackTotals = {
  schoolFee: { total_fee: 0, total_paid: 0, total_outstanding: 0 },
  miscFee:   { total_fee: 0, total_paid: 0, total_outstanding: 0 },
};

   const fetchExportTotals = async () => {
    try {
      const response = await schoolFeeAPI.getTotals();
      if (response.data.success) {
        return response.data.data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching export totals:', error);
      return null;
    }
  };

  const exportToExcel = async () => {
    try {
      const totals = await fetchExportTotals() || fallbackTotals ;
  
      const exportData = filteredSubmittedFees.flatMap(record => {
        const baseData = {
          'Student Name': record.studentName,
          'Class': record.class,
          'School Fee': formatNumber(getSchoolFeeByClass(record.class)),
          'School Fee Paid': formatNumber(record.amount_paid),
          'School Fee Outstanding': formatNumber(record.outstanding),
          'Misc Fee Name': 'N/A',
          'Misc Fee Amount': 'N/A',
          'Misc Fee Paid': 'N/A',
          'Misc Fee Outstanding': 'N/A',
          'Date': record.payment_date ? new Date(record.payment_date).toLocaleDateString() : 'No payment date available'
        };
  
        if (!record.miscFees || record.miscFees.length === 0) {
          return [baseData];
        }
  
        const rows = [baseData];
        
        record.miscFees.forEach(fee => {
          rows.push({
            'Student Name': '',
            'Class': '',
            'School Fee': '',
            'School Fee Paid': '',
            'School Fee Outstanding': '',
            'Misc Fee Name': fee.name,
            'Misc Fee Amount': formatNumber(fee.amount),
            'Misc Fee Paid': formatNumber(fee.paid),
            'Misc Fee Outstanding': formatNumber(fee.outstanding),
            'Date': ''
          });
        });
  
        return rows;
      });
  
      exportData.push({
        'Student Name': 'TOTAL',
        'Class': '',
        'School Fee': formatNumber(totals.schoolFee.total_fee),
        'School Fee Paid': formatNumber(totals.schoolFee.total_paid),
        'School Fee Outstanding': formatNumber(totals.schoolFee.total_outstanding),
        'Misc Fee Name': '',
        'Misc Fee Amount': formatNumber(totals.miscFee.total_fee),
        'Misc Fee Paid': formatNumber(totals.miscFee.total_paid),
        'Misc Fee Outstanding': formatNumber(totals.miscFee.total_outstanding),
        'Date': ''
      });
  
      const ws = XLSX.utils.json_to_sheet(exportData, {
        header: [
          'Student Name',
          'Class',
          'School Fee',
          'School Fee Paid',
          'School Fee Outstanding',
          'Misc Fee Name',
          'Misc Fee Amount',
          'Misc Fee Paid',
          'Misc Fee Outstanding',
          'Date'
        ]
      });
  
      const wscols = [
        { wch: 20 },
        { wch: 8 },
        { wch: 12 },
        { wch: 15 },
        { wch: 20 },
        { wch: 15 },
        { wch: 15 },
        { wch: 12 },
        { wch: 20 },
        { wch: 15 }
      ];
      ws['!cols'] = wscols;
  
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Fee Records");
      XLSX.writeFile(wb, `FeeRecords_${new Date().toISOString().slice(0,10)}.xlsx`);
    } catch (error) {
      showToastMessage(`Error exporting to Excel: ${error.message}`, 'danger');
    }
  };
  
  const exportToWord = async () => {
    try {
      const totals = await fetchExportTotals() || fallbackTotals ;
  
      const tableRows = filteredSubmittedFees.flatMap(record => {
        const baseRow = [
          new TableCell({ children: [new Paragraph(record.studentName)] }),
          new TableCell({ children: [new Paragraph(record.class)] }),
          new TableCell({ children: [new Paragraph(formatNumber(getSchoolFeeByClass(record.class)))] }),
          new TableCell({ children: [new Paragraph(formatNumber(record.amount_paid))] }),
          new TableCell({ children: [new Paragraph(formatNumber(record.outstanding))] }),
          new TableCell({ children: [new Paragraph("N/A")] }),
          new TableCell({ children: [new Paragraph("N/A")] }),
          new TableCell({ children: [new Paragraph("N/A")] }),
          new TableCell({ children: [new Paragraph("N/A")] }),
          new TableCell({ children: [new Paragraph(record.payment_date ? new Date(record.payment_date).toLocaleDateString() : 'No payment date available')] })
        ];
  
        if (!record.miscFees || record.miscFees.length === 0) {
          return [new TableRow({ children: baseRow })];
        }
  
        return [
          new TableRow({ children: baseRow }),
          ...record.miscFees.map(fee => new TableRow({
            children: [
              new TableCell({ children: [new Paragraph("")] }),
              new TableCell({ children: [new Paragraph("")] }),
              new TableCell({ children: [new Paragraph("")] }),
              new TableCell({ children: [new Paragraph("")] }),
              new TableCell({ children: [new Paragraph("")] }),
              new TableCell({ children: [new Paragraph(fee.name)] }),
              new TableCell({ children: [new Paragraph(formatNumber(fee.amount))] }),
              new TableCell({ children: [new Paragraph(formatNumber(fee.paid))] }),
              new TableCell({ children: [new Paragraph(formatNumber(fee.outstanding))] }),
              new TableCell({ children: [new Paragraph("")] })
            ]
          }))
        ];
      });
  
      tableRows.push(new TableRow({
        children: [
          new TableCell({ children: [new Paragraph("TOTAL")] }),
          new TableCell({ children: [new Paragraph("")] }),
          new TableCell({ children: [new Paragraph(formatNumber(totals.schoolFee.total_fee))] }),
          new TableCell({ children: [new Paragraph(formatNumber(totals.schoolFee.total_paid))] }),
          new TableCell({ children: [new Paragraph(formatNumber(totals.schoolFee.total_outstanding))] }),
          new TableCell({ children: [new Paragraph("")] }),
          new TableCell({ children: [new Paragraph(formatNumber(totals.miscFee.total_fee))] }),
          new TableCell({ children: [new Paragraph(formatNumber(totals.miscFee.total_paid))] }),
          new TableCell({ children: [new Paragraph(formatNumber(totals.miscFee.total_outstanding))] }),
          new TableCell({ children: [new Paragraph("")] })
        ]
      }));
  
      const doc = new Document({
        sections: [{
          children: [
            new Paragraph({
              children: [new TextRun({ text: "Submitted Fee Records", bold: true, size: 24 })],
              spacing: { after: 200 }
            }),
            new Table({
              rows: [
                new TableRow({
                  children: [
                    new TableCell({ children: [new Paragraph("Student Name")] }),
                    new TableCell({ children: [new Paragraph("Class")] }),
                    new TableCell({ children: [new Paragraph("School Fee")] }),
                    new TableCell({ children: [new Paragraph("School Fee Paid")] }),
                    new TableCell({ children: [new Paragraph("School Fee Outstanding")] }),
                    new TableCell({ children: [new Paragraph("Misc Fee Name")] }),
                    new TableCell({ children: [new Paragraph("Misc Fee Amount")] }),
                    new TableCell({ children: [new Paragraph("Misc Fee Paid")] }),
                    new TableCell({ children: [new Paragraph("Misc Fee Outstanding")] }),
                    new TableCell({ children: [new Paragraph("Date")] })
                  ]
                }),
                ...tableRows
              ]
            })
          ]
        }]
      });
  
      const blob = await Packer.toBlob(doc);
      saveAs(blob, `FeeRecords_${new Date().toISOString().slice(0, 10)}.docx`);
    } catch (error) {
      showToastMessage(`Error exporting to Word: ${error.message}`, 'danger');
    }
  };

  const exportToPDF = async () => {
  try {
    const totals = await fetchExportTotals() || fallbackTotals ;

    const exportData = filteredSubmittedFees.flatMap(record => {
      const baseData = [
        record.studentName,
        record.class,
        formatNumber(getSchoolFeeByClass(record.class)),
        formatNumber(record.amount_paid),
        formatNumber(record.outstanding),
        'N/A',
        'N/A',
        'N/A',
        'N/A',
        record.payment_date ? new Date(record.payment_date).toLocaleDateString() : 'No payment date available'
      ];

      if (!record.miscFees || record.miscFees.length === 0) {
        return [baseData];
      }

      return [
        baseData,
        ...record.miscFees.map(fee => [
          '',
          '',
          '',
          '',
          '',
          fee.name,
          formatNumber(fee.amount),
          formatNumber(fee.paid),
          formatNumber(fee.outstanding),
          ''
        ])
      ];
    });

    exportData.push([
      'TOTAL',
      '',
      formatNumber(totals.schoolFee.total_fee),
      formatNumber(totals.schoolFee.total_paid),
      formatNumber(totals.schoolFee.total_outstanding),
      '',
      formatNumber(totals.miscFee.total_fee),
      formatNumber(totals.miscFee.total_paid),
      formatNumber(totals.miscFee.total_outstanding),
      ''
    ]);

    const doc = new jsPDF({ orientation: 'landscape' });

    doc.setFontSize(18);
    doc.text('Submitted Fee Records', 14, 15);

    doc.autoTable({
      head: [[
        'Student Name', 'Class', 'School Fee', 'School Fee Paid', 'School Fee Outstanding',
        'Misc Fee Name', 'Misc Fee Amount', 'Misc Fee Paid', 'Misc Fee Outstanding', 'Date'
      ]],
      body: exportData,
      startY: 25,
      styles: {
        fontSize: 8,
        cellPadding: 2,
        overflow: 'linebreak'
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontSize: 9
      },
      margin: { left: 5, right: 5 }
    });

    doc.save(`FeeRecords_${new Date().toISOString().slice(0,10)}.pdf`);
  } catch (error) {
    showToastMessage(`Error exporting to PDF: ${error.message}`, 'danger');
  }
};

return (
  <div className="fex-dropdown">
    <button
      className="fex-toggle"
      onClick={() => setIsOpen(!isOpen)}
      aria-haspopup="true"
      aria-expanded={isOpen}
    >
      Export ▾
    </button>

    {isOpen && (
      <div className="fex-menu">
        <button className="fex-item" onClick={() => { exportToExcel(); setIsOpen(false); }}>
          <i className="bi bi-file-earmark-spreadsheet fex-item-icon" />
          Excel
        </button>
        <button className="fex-item" onClick={() => { exportToWord(); setIsOpen(false); }}>
          <i className="bi bi-file-earmark-word fex-item-icon" />
          Word
        </button>
        <button className="fex-item" onClick={() => { exportToPDF(); setIsOpen(false); }}>
          <i className="bi bi-file-earmark-pdf fex-item-icon" />
          PDF
        </button>
      </div>
    )}
  </div>
);



}

export default FeeExport;